import { type RefObject, useEffect } from "react";

/**
 * WebOTP autofill for Android Chrome.
 *
 * iOS Safari reads the SMS on its own (via `autocomplete="one-time-code"`), but Android only
 * surfaces the SMS code if the page *actively requests* it through the WebOTP API. This hook
 * makes that request: it asks the browser to watch for an incoming SMS and, when one arrives,
 * writes the code into the target input (and optionally auto-submits).
 *
 * Requirements for it to fire (all enforced by the browser, not us):
 *  - Chromium on Android, over HTTPS on a real domain (never http/localhost).
 *  - The SMS must end with a `@<host> #<code>` line whose host matches the page origin
 *    (produced by the backend build_webotp_sms_suffix).
 * Everywhere else (iOS, desktop, unsupported browsers) `OTPCredential` is absent and this
 * hook is a no-op, so it is always safe to mount.
 *
 * @param inputRef ref to the code <input> (uncontrolled — we set .value directly)
 * @param enabled  only arm the listener while the code step is visible
 * @param onFilled optional callback with the received code (e.g. to trigger submit)
 */
export function useWebOtpAutofill(inputRef: RefObject<HTMLInputElement | null>, enabled: boolean, onFilled?: (code: string) => void): void {
	useEffect(() => {
		// Feature-detect WebOTP. Absent on iOS/desktop/unsupported → nothing to do.
		if (!enabled || typeof window === "undefined" || !("OTPCredential" in window)) {
			return;
		}

		const abortController = new AbortController();

		navigator.credentials
			.get({
				// `otp` is not yet in the base TS DOM lib; the transport shape is per the WebOTP spec.
				otp: { transport: ["sms"] },
				signal: abortController.signal,
			} as CredentialRequestOptions)
			.then((credential) => {
				// OTPCredential carries the parsed code in `.code`; it is not in the base TS types.
				const code = (credential as (Credential & { code?: string }) | null)?.code;
				if (!code) {
					return;
				}

				const input = inputRef.current;
				if (input) {
					// Uncontrolled input: set the DOM value and fire `input` so React/MUI + FormData see it.
					input.value = code;
					input.dispatchEvent(new Event("input", { bubbles: true }));
				}
				onFilled?.(code);
			})
			.catch((error: unknown) => {
				// AbortError on unmount is expected; anything else (e.g. user dismissed) is non-fatal.
				if (error instanceof DOMException && error.name === "AbortError") {
					return;
				}
				console.debug("WebOTP autofill unavailable or dismissed", error);
			});

		return () => abortController.abort();
	}, [inputRef, enabled, onFilled]);
}
